import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, StyleSheet, FlatList, NativeSyntheticEvent, NativeScrollEvent, ListRenderItem } from 'react-native';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { SearchBar, InlineEmptyMessage, LoadingSpinner, AdminBulletinCard, BulletinFeedTabs, BulletinRecipientTabs } from '@/components';
import { EMPTY_MESSAGES } from '@/constants/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { getBulletinsEmptyMessage } from '@/utils/admin/bulletinUtils';
import { IBulletinTableRow } from '@/types/admin/bulletins';
import { RecipientType } from '@/components/admin/bulletins/BulletinRecipientTabs';
import { BulletinFeedFilter } from '@/types/admin/bulletins';
import { useAccumulatedPaging } from '@/hooks/common/useAccumulatedPaging';

interface DesktopBulletinsViewProps {
  tableRows: IBulletinTableRow[];
  isLoading: boolean;
  isFetchingMore?: boolean;
  isTabSwitching?: boolean;
  enhancedError: { userFriendlyMessage?: string } | null;
  retryCount: number;
  canRetry: boolean;
  manualRetry: () => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  feedFilter: BulletinFeedFilter;
  onFeedFilterChange: (filter: BulletinFeedFilter) => void;
  recipientType: RecipientType;
  onRecipientTypeChange: (recipient: RecipientType) => void;
  hasNextPage: boolean;
  onLoadMore: () => void;
  refresh: () => void;
  onCreateBulletin: () => void;
  onEditBulletin: (row: IBulletinTableRow) => void;
}

export const DesktopBulletinsView: React.FC<DesktopBulletinsViewProps> = ({
  tableRows,
  isLoading,
  isFetchingMore,
  isTabSwitching = false,
  enhancedError,
  retryCount,
  canRetry,
  manualRetry,
  searchQuery,
  onSearchChange,
  feedFilter,
  onFeedFilterChange,
  recipientType,
  onRecipientTypeChange,
  hasNextPage,
  onLoadMore,
  refresh,
  onCreateBulletin,
  onEditBulletin,
}) => {
  const { redesign } = useAdminColors();
  const listRef = useRef<FlatList<IBulletinTableRow> | null>(null);
  const accumulationKey = useMemo(() => `${feedFilter}|${recipientType}|${searchQuery.trim()}`, [feedFilter, recipientType, searchQuery]);
  const { accumulatedRows, clearIfPendingReplaceAndNoRows } = useAccumulatedPaging<IBulletinTableRow>({
    // When switching tabs, pass empty rows to avoid re-accumulating stale data
    rows: isTabSwitching ? [] : tableRows,
    accumulationKey,
  });

  useEffect(() => {
    clearIfPendingReplaceAndNoRows(isLoading);
  }, [isLoading, tableRows, accumulationKey, clearIfPendingReplaceAndNoRows]);

  const hasData = (accumulatedRows || []).length > 0;
  const hasSearch = searchQuery.trim().length > 0;

  const emptyMessage = useMemo(() => getBulletinsEmptyMessage(feedFilter), [feedFilter]);

  const onEndReached = useCallback(() => {
    if (hasNextPage && !isLoading && !isFetchingMore) {
      onLoadMore();
    }
  }, [hasNextPage, isLoading, isFetchingMore, onLoadMore]);

  const noopToggle = useCallback((_id: number) => {
    // no-op on desktop for now; stable reference for memo
  }, []);

  const renderItem: ListRenderItem<IBulletinTableRow> = useCallback(({ item }) => (
    <AdminBulletinCard
      bulletin={item}
      onEdit={onEditBulletin}
      onToggleExpanded={noopToggle}
      feedFilter={feedFilter}
    />
  ), [feedFilter, onEditBulletin, noopToggle]);

  const renderData = () => {
    if (isTabSwitching) {
      return (
        <View style={styles.spinnerWrap}>
          <LoadingSpinner message="Switching tabs..." />
        </View>
      );
    }
    if (isLoading && !hasData && !hasSearch) {
      return (
        <View style={styles.spinnerWrap}>
          <LoadingSpinner message="Loading bulletins..." />
        </View>
      );
    }

    if (enhancedError && !hasData && !isLoading && !hasSearch) {
      return (
        <View style={styles.errorWrap}>
          <InlineEmptyMessage
            title={'Unable to Load Bulletins'}
            message={enhancedError.userFriendlyMessage || 'Something went wrong while loading the bulletins.'}
            icon={'X_CIRCLE' as any}
          />
        </View>
      );
    }

    if (!hasData) {
      if (hasSearch) {
        const cfg = EMPTY_MESSAGES.bulletins.noResults(searchQuery);
        return (
          <InlineEmptyMessage
            title={cfg.title}
            message={cfg.message}
            icon={cfg.icon}
            containerStyle={styles.noResultsContainer}
            titleStyle={{ ...styles.noResultsTitle, color: redesign.text.primary }}
            messageStyle={{ ...styles.noResultsMessage, color: redesign.text.muted }}
          />
        );
      }
      return (
        <InlineEmptyMessage
          title={emptyMessage.title}
          message={emptyMessage.message}
          icon={'MESSAGE_BLAST'}
          containerStyle={styles.emptyContainer}
          titleStyle={{ ...styles.emptyTitle, color: redesign.text.primary }}
          messageStyle={{ ...styles.emptyMessage, color: redesign.text.muted }}
        />
      );
    }

    return (
      <View style={styles.centerColumn}>
        <FlatList
          ref={listRef as any}
          data={accumulatedRows}
          keyExtractor={(item) => `${item.id}-${item.updatedAt || item.date?.toISOString()}`}
          renderItem={renderItem}
          style={styles.list}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          onEndReachedThreshold={0.4}
          onEndReached={onEndReached}
          ListFooterComponent={isFetchingMore && hasData ? (
            <View style={styles.inlineSpinner}>
              <LoadingSpinner message="Loading more..." showMessage={false} />
            </View>
          ) : null}
        />
      </View>
    );
  };

  return (
    <DesktopAdminLayout>
      <DesktopPageHeader title="Bulletins" showAddButton onAddPress={onCreateBulletin} addButtonText="New Bulletin">
        <View style={styles.headerSearch}>
          <SearchBar placeholder="Search" value={searchQuery} onChangeText={onSearchChange} />
        </View>
      </DesktopPageHeader>

      <View style={styles.filtersRow}>
        <BulletinRecipientTabs activeRecipientType={recipientType} onRecipientTypeChange={onRecipientTypeChange} />
        <BulletinFeedTabs activeFilter={feedFilter} onFilterChange={onFeedFilterChange} />
      </View>

      {renderData()}

      {/* Create/Edit use full screens via navigation on desktop now */}
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  headerSearch: {
    minWidth: 260,
  },
  filtersRow: {
    gap: 12,
    marginBottom: 12,
  },
  spinnerWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 320,
  },
  errorWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 320,
    padding: 24,
  },
  list: {
    flex: 1,
  },
  listContent: {
    paddingBottom: 24,
  },
  centerColumn: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    maxWidth: 720,
    alignSelf: 'center',
    gap: 12,
  },
  inlineSpinner: {
    paddingVertical: 16,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 24,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 8,
  },
  emptyMessage: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 360,
  },
  noResultsContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 20,
  },
  noResultsTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  noResultsMessage: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 360,
  },
});


